import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, SlidersHorizontal, Download, Bookmark, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { useRefs, versOptions, listeOptions, telecharger } from "@/lib/referentiels";
import { EnTete, Vide, Champ } from "@/components/commun";
import { FicheCarte } from "@/components/FicheCarte";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const Filtres = ({ ouvert, setOuvert, criteres, setCriteres, onEnregistrer }) => {
  const refs = useRefs();
  const { user } = useAuth();
  const [processus, setProcessus] = useState([]);
  const [tags, setTags] = useState([]);
  const [nom, setNom] = useState("");
  useEffect(() => { api.get("/processus").then((r) => setProcessus(r.data)); api.get("/tags").then((r) => setTags(r.data)); }, []);
  const m = (k) => (v) => setCriteres({ ...criteres, [k]: v?.target ? v.target.value : v });
  const etapes = processus.find((p) => p.id === criteres.processus_id)?.etapes || [];
  return (
    <Sheet open={ouvert} onOpenChange={setOuvert}>
      <SheetContent side="right" className="at-popover !rounded-none w-full sm:max-w-md overflow-y-auto" data-testid="filters-sheet">
        <SheetHeader><SheetTitle className="text-left">Filtres</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-4">
          <ChoixListe label="Processus" valeur={criteres.processus_id} onChange={(v) => setCriteres({ ...criteres, processus_id: v, etape: "" })} options={processus.map((p) => ({ code: p.id, label: p.nom }))} testId="filter-processus" placeholder="Tous" />
          {etapes.length > 0 && <ChoixListe label="Étape" valeur={criteres.etape} onChange={m("etape")} options={etapes.map((e) => ({ code: e.code, label: e.nom }))} testId="filter-etape" placeholder="Toutes" />}
          {["dirigeant", "responsable"].includes(user.role) && <ChoixListe label="Agent" valeur={criteres.agent_id} onChange={m("agent_id")} options={(refs?.equipe || []).filter((u) => u.role === "agent").map((u) => ({ code: u.id, label: u.nom }))} testId="filter-agent" placeholder="Tous" />}
          <ChoixListe label="Opération" valeur={criteres.operation} onChange={m("operation")} options={listeOptions(refs?.operations)} testId="filter-operation" placeholder="Toutes" />
          <ChoixListe label="Source" valeur={criteres.source} onChange={m("source")} options={listeOptions(refs?.sources)} testId="filter-source" placeholder="Toutes" />
          <ChoixListe label="Tag" valeur={criteres.tag} onChange={m("tag")} options={tags.map((t) => ({ code: t.id, label: t.nom }))} testId="filter-tag" placeholder="Tous" />
          <ChoixListe label="Statut" valeur={criteres.statut} onChange={m("statut")} options={versOptions(refs?.statuts)} testId="filter-statut" placeholder="Tous" />
          <Champ label="Département" id="f-dep" value={criteres.departement || ""} onChange={m("departement")} placeholder="ex. 29" data-testid="filter-departement" />
          <div className="grid grid-cols-2 gap-2">
            <Champ label="Créée du" id="f-d1" type="date" value={criteres.date_debut || ""} onChange={m("date_debut")} data-testid="filter-date-debut" />
            <Champ label="au" id="f-d2" type="date" value={criteres.date_fin || ""} onChange={m("date_fin")} data-testid="filter-date-fin" />
          </div>
          <label className="flex items-center gap-3 min-h-[44px] text-sm font-semibold"><input type="checkbox" checked={Boolean(criteres.sans_action)} onChange={(e) => m("sans_action")(e.target.checked)} className="w-5 h-5" data-testid="filter-sans-action" />Sans action uniquement</label>
          <button type="button" className="at-btn at-btn-ghost w-full" onClick={() => setCriteres({})} data-testid="filters-reset">Réinitialiser</button>
          <div className="flex gap-2 pt-2">
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom du filtre" className="at-input" data-testid="filter-save-name" />
            <button type="button" disabled={!nom.trim()} onClick={() => { onEnregistrer(nom); setNom(""); }} className="at-btn at-btn-primary shrink-0" data-testid="filter-save-button">Enregistrer</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function Fiches() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const champ = useRef(null);
  const [criteres, setCriteres] = useState(() => ({ recherche: params.get("recherche") || "", favoris: params.get("favoris") === "1" }));
  const [fiches, setFiches] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [mesFiltres, setMesFiltres] = useState([]);
  const chargerFiltres = useCallback(() => api.get("/filtres").then((r) => setMesFiltres(r.data)), []);
  const actifs = Object.entries(criteres).filter(([k, v]) => k !== "recherche" && v).length;
  const nettoyer = (c) => Object.fromEntries(Object.entries(c).filter(([, v]) => v));

  useEffect(() => { setCriteres((c) => ({ ...c, recherche: params.get("recherche") || c.recherche, favoris: params.get("favoris") === "1" })); }, [params]);
  useEffect(() => { chargerFiltres(); }, [chargerFiltres]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/fiches", { params: nettoyer(criteres) }).then((r) => setFiches(r.data)), 250);
    return () => clearTimeout(t);
  }, [criteres]);

  const enregistrer = async (nom) => {
    try { await api.post("/filtres", { nom, criteres: nettoyer(criteres) }); toast.success("Filtre enregistré dans « Mes filtres »."); chargerFiltres(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const exporter = (format) => telecharger("/export/fiches", { ...nettoyer(criteres), format }, `fiches-airthermo.${format}`).catch(() => toast.error("Export impossible."));

  return (
    <div data-testid="page-fiches">
      <EnTete titre={criteres.favoris ? "Favoris" : "Fiches"} sousTitre={fiches ? `${fiches.length} fiche${fiches.length > 1 ? "s" : ""}` : " "} />
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-xl">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 at-muted" />
          <input ref={champ} value={criteres.recherche || ""} onChange={(e) => setCriteres({ ...criteres, recherche: e.target.value })} placeholder="Rechercher…"
            className="at-input !pl-11 !bg-[var(--surface)]" style={{ boxShadow: "var(--shadow)" }} data-testid="fiches-search-input" />
        </div>
        <button type="button" onClick={() => setOuvert(true)} className="at-btn at-btn-ghost" data-testid="filters-open-button"><SlidersHorizontal size={16} />Filtres{actifs ? ` (${actifs})` : ""}</button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button type="button" className="at-btn at-btn-ghost" data-testid="saved-filters-button"><Bookmark size={16} />Mes filtres</button></DropdownMenuTrigger>
          <DropdownMenuContent className="at-popover w-64 p-2">
            {mesFiltres.length === 0 && <p className="text-sm at-muted p-2">Aucun filtre enregistré.</p>}
            {mesFiltres.map((f) => (
              <div key={f.id} className="flex items-center">
                <DropdownMenuItem className="flex-1 min-h-[44px] rounded-xl cursor-pointer" onClick={() => setCriteres({ recherche: "", ...f.criteres })} data-testid={`saved-filter-${f.id}`}>{f.nom}</DropdownMenuItem>
                <button type="button" aria-label="Supprimer le filtre" className="at-icon-btn" onClick={() => api.delete(`/filtres/${f.id}`).then(chargerFiltres)} data-testid={`saved-filter-delete-${f.id}`}><X size={15} /></button>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {user.role !== "partenaire" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button type="button" className="at-btn at-btn-ghost" data-testid="export-button"><Download size={16} />Exporter</button></DropdownMenuTrigger>
            <DropdownMenuContent className="at-popover p-2">
              <DropdownMenuItem className="min-h-[44px] rounded-xl cursor-pointer" onClick={() => exporter("csv")} data-testid="export-csv">CSV</DropdownMenuItem>
              <DropdownMenuItem className="min-h-[44px] rounded-xl cursor-pointer" onClick={() => exporter("xlsx")} data-testid="export-xlsx">Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {fiches && fiches.length === 0 && <Vide texte="Aucune fiche ne correspond." testId="fiches-empty" />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="fiches-list">{fiches?.map((f) => <FicheCarte key={f.id} fiche={f} />)}</div>
      <Filtres ouvert={ouvert} setOuvert={setOuvert} criteres={criteres} setCriteres={setCriteres} onEnregistrer={enregistrer} />
    </div>
  );
}
